<template>
  <div class="whatsapp-thread-card">
    <div class="whatsapp-thread-card__header">
      <button v-if="showBackButton" class="whatsapp-thread-card__back-btn" @click="$emit('back')">
        <Icon icon="mdi:chevron-left" />
      </button>
      <div class="whatsapp-thread-card__header-content">
        <h5
          class="whatsapp-thread-card__contact-name whatsapp-thread-card__contact-name--clickable"
          @click="handleContactNameClick"
          title="View profile"
        >
          {{ contactName || "Contact Name" }}
        </h5>
        <div v-if="lastSeen || conversation?.isActive" class="whatsapp-thread-card__status-wrapper">
          <span v-if="conversation?.isActive" class="whatsapp-thread-card__status">
            <span
              class="whatsapp-thread-card__status-dot whatsapp-thread-card__status-dot--active"
            ></span>
            Active now
          </span>
          <span v-else-if="lastSeen" class="whatsapp-thread-card__status">
            <span class="whatsapp-thread-card__status-dot"></span>
            {{ lastSeen }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="whatsapp-thread-card__loading">
      <div class="whatsapp-thread-card__loading-content">
        <LoadingBars size="sm" />
      </div>
    </div>

    <div v-else ref="parentRef" class="whatsapp-thread-card__messages" @scroll="handleScroll">
      <div v-if="unipileMessages.length === 0" class="whatsapp-thread-card__empty">
        <Icon icon="mdi:message-outline" class="whatsapp-thread-card__empty-icon" />
        <p>No messages yet</p>
      </div>
      <template v-else>
        <div v-if="isLoadingOlderMessages" class="whatsapp-thread-card__loading-older">
          <Icon icon="mdi:loading" class="whatsapp-thread-card__loading-older-icon" />
          <span>Loading...</span>
        </div>
        <div
          v-else-if="!hasMoreMessages && unipileMessages.length > 0"
          class="whatsapp-thread-card__no-more"
        >
          <span>No more messages</span>
        </div>

        <div
          :style="{
            height: `${totalSize}px`,
            width: '100%',
            position: 'relative',
          }"
        >
          <div
            v-for="virtualRow in virtualRows"
            :key="unipileMessages[virtualRow.index]?.id || virtualRow.index"
            :ref="(el) => measureElement(el as HTMLElement | null, virtualRow.index)"
            :data-index="virtualRow.index"
            :style="{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            }"
          >
            <div
              class="whatsapp-thread-card__message-wrapper"
              :class="{
                'whatsapp-thread-card__message-wrapper--highlighted':
                  highlightedMessageId === unipileMessages[virtualRow.index]?.id,
              }"
            >
              <div
                v-if="shouldShowDateSeparator(virtualRow.index)"
                class="whatsapp-thread-card__date-separator"
              >
                <span>{{ formatDateSeparator(unipileMessages[virtualRow.index].timestamp) }}</span>
              </div>
              <MessageItem
                :message="unipileMessages[virtualRow.index]"
                platform="whatsapp"
                :show-sender="true"
                :show-read-receipts="true"
                :participants="participants"
                @reply="$emit('reply', unipileMessages[virtualRow.index])"
                @ai-reply="$emit('ai-reply', unipileMessages[virtualRow.index])"
                @ai-followup="$emit('add-followup')"
                @forward="$emit('forward', unipileMessages[virtualRow.index])"
                @delete="$emit('delete', unipileMessages[virtualRow.index])"
                @react="$emit('react', unipileMessages[virtualRow.index], $event)"
                @remove-reaction="$emit('remove-reaction', unipileMessages[virtualRow.index])"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import MessageItem from "@/components/socials/MessageItem.vue";
import type { Message, ChatParticipant, Conversation } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { computed, ref, type ComputedRef } from "vue";
import { useVirtualizer } from "@tanstack/vue-virtual";
import { useMessageScroll } from "@/composables/messages/useMessageScroll";

interface LegacyMessage {
  type: "outgoing" | "incoming";
  author: string;
  timestamp: string | Date;
  content: string;
}

interface Props {
  contactName?: string;
  contactId?: string;
  lastSeen?: string;
  messages?: LegacyMessage[];
  unipileMessages?: Message[];
  isLoading?: boolean;
  isLoadingOlderMessages?: boolean;
  hasMoreMessages?: boolean;
  participants?: ChatParticipant[];
  conversation?: Conversation | null;
  showBackButton?: boolean;
  highlightMessageId?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  contactName: "",
  contactId: "",
  lastSeen: "",
  messages: () => [],
  unipileMessages: () => [],
  isLoading: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: true,
  conversation: null,
  showBackButton: false,
  highlightMessageId: null,
});

const emit = defineEmits<{
  reply: [message?: Message];
  "ai-reply": [message?: Message];
  "add-followup": [];
  forward: [message: Message];
  delete: [message: Message];
  react: [message: Message, reaction: string];
  "remove-reaction": [message: Message];
  "view-contact-profile": [contactId: string];
  "load-older-messages": [];
  back: [];
}>();

const handleContactNameClick = () => {
  if (props.contactId) {
    emit("view-contact-profile", props.contactId);
  }
};

const parentRef = ref<HTMLElement | null>(null);

const unipileMessages = computed(() => {
  let messages: Message[];
  if (props.unipileMessages && props.unipileMessages.length > 0) {
    messages = props.unipileMessages;
  } else {
    messages = props.messages.map((msg) => ({
      id: `legacy-${msg.timestamp}`,
      conversationId: "",
      senderId: msg.type === "outgoing" ? "me" : "contact",
      senderName: msg.author,
      content: msg.content,
      channel: "whatsapp",
      timestamp: typeof msg.timestamp === "string" ? msg.timestamp : msg.timestamp.toISOString(),
      isOwn: msg.type === "outgoing",
    })) as Message[];
  }
  return messages;
});

const shouldShowDateSeparator = (index: number): boolean => {
  if (index === 0) return true;
  const message = unipileMessages.value[index];
  const prevMessage = unipileMessages.value[index - 1];
  if (!message || !prevMessage) return false;
  const currentDate = new Date(message.timestamp).toDateString();
  const previousDate = new Date(prevMessage.timestamp).toDateString();
  return currentDate !== previousDate;
};

const formatDateSeparator = (timestamp: string): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "TODAY";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "YESTERDAY";
  }
  return date
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();
};

const conversationKey = computed(() => {
  if (props.conversation?.id) {
    return props.conversation.id;
  }
  if (unipileMessages.value.length > 0 && unipileMessages.value[0].conversationId) {
    return unipileMessages.value[0].conversationId;
  }
  return "default";
});

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: unipileMessages.value.length,
    getScrollElement: () => parentRef.value,
    estimateSize: () => 72,
    overscan: 8,
    getItemKey: (index: number) => {
      const msg = unipileMessages.value[index] as any;
      return msg?._stableKey || msg?.id || index;
    },
  }))
);

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

const measureElement = (el: HTMLElement | null, _index: number) => {
  if (el) {
    rowVirtualizer.value.measureElement(el);
  }
};

const { handleScroll, highlightedMessageId } = useMessageScroll({
  parentRef,
  rowVirtualizer: rowVirtualizer as any,
  virtualRows,
  messages: unipileMessages as ComputedRef<Array<{ id: string }>>,
  conversationKey,
  isLoading: computed(() => props.isLoading),
  isLoadingOlderMessages: computed(() => props.isLoadingOlderMessages),
  hasMoreMessages: computed(() => props.hasMoreMessages),
  onLoadOlderMessages: () => emit("load-older-messages"),
  highlightMessageId: computed(() => props.highlightMessageId),
});
</script>

<style scoped>
.whatsapp-thread-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #ffffff;
  position: relative;
}

.whatsapp-thread-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
}

.whatsapp-thread-card__back-btn {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 2px;
  border-radius: 4px;
  font-size: 1.25rem;
}

.whatsapp-thread-card__back-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.whatsapp-thread-card__header-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.whatsapp-thread-card__contact-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  margin: 0;
}

.whatsapp-thread-card__contact-name--clickable {
  cursor: pointer;
}

.whatsapp-thread-card__status-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.whatsapp-thread-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
  margin: 0;
}

.whatsapp-thread-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  flex-shrink: 0;
}

.whatsapp-thread-card__status-dot--active {
  background: #10b981;
}

.whatsapp-thread-card__loading {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.whatsapp-thread-card__loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.whatsapp-thread-card__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
  background: #ffffff;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.whatsapp-thread-card__messages::-webkit-scrollbar {
  width: 5px;
}

.whatsapp-thread-card__messages::-webkit-scrollbar-track {
  background: transparent;
}

.whatsapp-thread-card__messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.whatsapp-thread-card__messages::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.whatsapp-thread-card__loading-older {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
  flex-shrink: 0;
}

.whatsapp-thread-card__loading-older-icon {
  animation: spin 1s linear infinite;
}

.whatsapp-thread-card__no-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
  flex-shrink: 0;
}

.whatsapp-thread-card__message-wrapper {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.whatsapp-thread-card__message-wrapper--highlighted {
  background-color: rgba(37, 211, 102, 0.15);
}

.whatsapp-thread-card__date-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0;
  position: relative;
}

.whatsapp-thread-card__date-separator::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}

.whatsapp-thread-card__date-separator span {
  background: #ffffff;
  padding: 0 0.75rem;
  font-size: 0.75rem;
  color: #6b7280;
  position: relative;
  z-index: 1;
}

.whatsapp-thread-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 0.875rem;
  padding: 2rem;
}

.whatsapp-thread-card__empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.whatsapp-thread-card__footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.whatsapp-thread-card__ai-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.whatsapp-thread-card__ai-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}
</style>
